import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment } from '@core/services/common';
import { PoliciesContextService } from '@core/services/context/policies-context.service';
import { Device } from '@ionic-native/device/ngx';
import { Platform } from '@ionic/angular';
import { Address } from '@models/address';
import { EnabledPolicyToChangeBankAccount } from '@models/bank-data';
import { BusinessCertificateInfo } from '@models/business';
import { DocumentROParams, DocumentTypes } from '@models/document';
import { Family } from '@models/family';
import { MailMessage } from '@models/mail';
import { NonRenewalROParams } from '@models/non-renewal';
import { ClientRegister, LoginWithCertificateOperation } from '@models/operation-register-businesses';
import {
  PolicyCar,
  SuperbonusOfferType,
  SuperbonusStatus
} from '@models/policy-car';
import { PolicyCollectiveLife } from '@models/policy-collective-life';
import { PolicyHealth } from '@models/policy-health';
import { PolicyHome } from '@models/policy-home';
import { PolicyInvestment } from '@models/policy-investment';
import { PolicyLife } from '@models/policy-life';
import { PolicyPension } from '@models/policy-pension';
import { PolicySaving } from '@models/policy-saving';
import { ClientAreaConfigurableCard, PolicyBaseEntry, ProductCard } from '@models/products';
import { TpvPaymentType } from '@models/receipt';
import {
  RGPDUERightSelectionData,
  RGPDUERightSelectOptions
} from '@models/rgpdue';
import { ForgotPasswordOrigin, TeCuidamosRegister } from '@models/user';
import { MedicalAccessOptionType, MedicalExaminationState } from '@modules/medical-checkups/interfaces';
import { RECEIPT_PAYMENT_COMPANY_DEPARTMENT_DATA, RECEIPT_PAYMENT_DEPARTMENT_DATA } from '@modules/receipt-payment/receipt-payment-constants';
import { GetUserDataResponse, RegisterState } from '@modules/register/models/register';
import { User } from '@sentry/types';
import { createHttpParams, getUserAgent, jsonStringify, padZeros } from '@utils/utils';
import * as moment from 'moment';
import {
  AutoOperation,
  CarSinisterConsultationOperation,
  FamiliesOperation,
  HealthOperation,
  HelpOperation,
  HomeOperation,
  LifeOperation, MultiMapOperation, OthersOperation,
  PaymentOperation,
  PaymentPlanOperation,
  PenInvOperation,
  PolicyBaseTypeToProductId,
  RGPDUEPWAPatrimonials,
  RgpdueRights,
  RoadAssistanceOperation,
  SuperbonusOperation,
  TechOperation,
  TeCuidamosOperation
} from '../../models/operation-register';
import { PaymentPlan } from '../../models/payment-plan';
import { Policy, PolicyBaseType } from '../../models/policy';
import { PatrimonialContextService } from '../context/patrimonial-context.service';
import { AddressHelperService } from '../helpers/address-helper.service';
import { AuthMirrorService } from './auth/auth-mirror.service';
import { AuthType } from './auth/auth.actions';
import { BaseService } from './base.service';
import { ContextService } from './context.service';
import { LogService } from './log.service';
import { MMobileHelperService } from './mmobile-helper.service';

enum Origin {
  APP = 6,
  WEB_DESKTOP = 5,
  WEB_RESPONSIVE = 7,
  ROBOT = 1
}

export const BUSINESS_OPERATION_CODE_PREFIX = '15';

@Injectable({
  providedIn: 'root'
})
export class OperationRegisterService {
  private isRobotOrigin = false;
  constructor(
    private authMirror: AuthMirrorService,
    private http: HttpClient,
    private mmobileHelper: MMobileHelperService,
    private base: BaseService,
    private platform: Platform,
    private addressHelper: AddressHelperService,
    private log: LogService,
    private device: Device,
    private patrimonialContext: PatrimonialContextService,
    private ctx: ContextService,
    private policiesCtx: PoliciesContextService,
  ) {}

  getOrigin() {
    if (this.isRobotOrigin) { return Origin.ROBOT; }

    if (this.ctx.isApp) { return Origin.APP; }

    if (this.platform.is('desktop')) { return Origin.WEB_DESKTOP; }

    return Origin.WEB_RESPONSIVE;
  }

  private buildOperationRequestBody(
    entityCod: string,
    productId: string,
    productCod: string,
    operationCod: string,
    groupingCod: string,
    opStructure: string,
    inputDocumentNumber?: any,
    email?: string,
    policy?: Policy
  ): any {
    const date = moment().format('YYYY-MM-DD');
    const { user } = this.authMirror.authService.getState();
    let { userCode, documentNumber } = user || ({} as any);

    documentNumber = (
      inputDocumentNumber ||
      documentNumber ||
      ''
    ).toUpperCase();

    if (this.authMirror.authService.checkUserIsCompany()) {
      documentNumber = this.buildRoBusinessDocumentNumberField();
      operationCod = this.buildRoBusinessOperationCodField(operationCod);
      userCode = this.buildRoBusinessUserCodeField();
      entityCod = this.buildRoBusinessEntityField(policy, entityCod, groupingCod);
      groupingCod = this.buildRoBusinessGroupField(policy, entityCod, groupingCod);
    }

    let productIdModify =
      productId != null && productId !== undefined ? productId.trim() : '';
    if (productIdModify.length > 85) {
      productIdModify = productIdModify.slice(0, 85);
    }
    if (opStructure.length > 4000) {
      opStructure = opStructure.slice(0, 4000);
    }
    const body: any = {
      entityCode: entityCod,
      productId: productIdModify,
      productCode: productCod,
      initDate: date,
      endingDate: date,
      operationCode: operationCod,
      groupingCode: groupingCod,
      structure: opStructure,
      documentNumber,
      origin: this.getOrigin(),
      userCode
    };
    if (email) {
      body.email = email;
    }
    if (user?.nuumaSim) {
      body.nuuma = user?.nuumaSim;
    }
    return body;
  }

  private register(
    entityCode: string,
    productId: string,
    productCode: string,
    operationCode: string,
    groupingCode: string,
    structure: string,
    documentNumber?: string,
    email?: string,
    policy?: Policy
  ) {
    const url = `${this.base.getPublicBaseUrl()}/operationsRegistry/registerOperation`;
    const body = this.buildOperationRequestBody(
      entityCode,
      productId,
      productCode,
      operationCode,
      groupingCode,
      structure,
      documentNumber,
      email,
      policy
    );
    return this.http.post(url, body, this.base.getPublicHeaders()).toPromise();
  }

  private registerFailSafe(
    entityCode: string,
    productId: string,
    productCode: string,
    operationCode: string,
    groupingCode: string,
    structure: string,
    documentNumber?: string,
    email?: string,
    policy?: Policy
  ) {
    return this.register(
      entityCode,
      productId,
      productCode,
      operationCode,
      groupingCode,
      structure,
      documentNumber,
      email,
      policy
    ).catch(err => this.log.e(this, 'Error trying to register operation', err));
  }

  private isPolicyConfigurationRoAvailable(policy: Policy) {
    const entityAvailable = !!policy?.configuration?.cardConfiguration?.ro?.entityCode;
    const groupAvailable = !!policy?.configuration?.cardConfiguration?.ro?.groupingCode;
    return  entityAvailable && groupAvailable;
  }

  private isGenericOperation(entityCod: string, groupingCod: string) {
    return entityCod === '00' && groupingCod === '0';
  }

  private buildRoBusinessDocumentNumberField() {
    const { user } = this.authMirror.authService.getState();
    let { documentNumber: cifDocumentNumber, legalRepresentative: { documentNumber: legalRepresentativeDocNumber } } = user || ({} as any);
    legalRepresentativeDocNumber = (legalRepresentativeDocNumber || '').toUpperCase();
    cifDocumentNumber = (cifDocumentNumber || '').toUpperCase();
    const businessDocumentNumberField = legalRepresentativeDocNumber + '+' + cifDocumentNumber;
    return businessDocumentNumberField;
  }

  private buildRoBusinessOperationCodField(operationCod?: string) {
    if (operationCod) {
      const rawOperationCode = operationCod.slice(-4);
      const businessOperationCod = BUSINESS_OPERATION_CODE_PREFIX + rawOperationCode;
      return businessOperationCod;
    }
    return '';
  }

  private buildRoBusinessUserCodeField() {
    return '';
  }

  private buildRoBusinessEntityField(policy: Policy, entityCod, groupingCod) {
    // We are assuming that every non-generic operation depends on the entityCod and groupingCod associated to a policy
    if (this.isPolicyConfigurationRoAvailable(policy) && !this.isGenericOperation(entityCod, groupingCod)) {
      return policy?.configuration?.cardConfiguration?.ro?.entityCode;
    }
    return entityCod;
  }

  private buildRoBusinessGroupField(policy: Policy, entityCod, groupingCod) {
    // We are assuming that every non-generic operation depends on the entityCod and groupingCod associated to a policy
    if (this.isPolicyConfigurationRoAvailable(policy) && !this.isGenericOperation(entityCod, groupingCod)) {
      return policy?.configuration?.cardConfiguration?.ro?.groupingCode;
    }
    return groupingCod;
  }

  registerAccessPolicyDetailOk(type: PolicyBaseType, policy) {
    if (!type || !policy) {
      return;
    }
    switch (type) {
      case 'home':
        this.registerHomeOperation(HomeOperation.PRODUCTO, policy, '');
        break;
      case 'accident':
      case 'death':
      case 'other':
        this.registerOthersOperation(OthersOperation.PRODUCTO, policy, '');
        break;
      case 'car':
        this.registerAutoOperation(AutoOperation.PRODUCTO, policy, '');
        break;
      case 'saving':
      case 'life':
        this.registerLifeOperation(LifeOperation.PRODUCTO, policy, '');
        break;
      case 'collective-life':
        this.registerLifeOperation(LifeOperation.PRODUCTO, policy, '');
        break;
      case 'pension':
        this.registerPensionOperation(PenInvOperation.PRODUCTO, policy, '');
        break;
      case 'investment':
      case 'investment-nova':
        this.registerInvestmentOperation(PenInvOperation.PRODUCTO, policy, '');
        break;
      case 'health':
        this.registerHealthOperation(HealthOperation.PRODUCTO, policy, '');
        break;
      default:
        break;
    }
  }

  registerAccessPolicyDetailKo(type: PolicyBaseType, policy) {
    if (!type || !policy) {
      return;
    }
    switch (type) {
      case 'home':
        this.registerHomeOperation(HomeOperation.PRODUCTO_ERROR, policy, '');
        break;
      case 'accident':
      case 'death':
      case 'other':
        this.registerOthersOperation(
          OthersOperation.PRODUCTO_ERROR,
          policy,
          ''
        );
        break;
      case 'car':
        this.registerAutoOperation(AutoOperation.PRODUCTO_ERROR, policy, '');
        break;
      case 'saving':
      case 'life':
        this.registerLifeOperation(LifeOperation.PRODUCTO_ERROR, policy, '');
        break;
      case 'pension':
        this.registerPensionOperation(
          PenInvOperation.PRODUCTO_ERROR,
          policy,
          ''
        );
        break;
      case 'investment':
      case 'investment-nova':
        this.registerInvestmentOperation(
          PenInvOperation.PRODUCTO_ERROR,
          policy,
          ''
        );
        break;
      case 'health':
        this.registerHealthOperation(
          HealthOperation.PRODUCTO_ERROR,
          policy,
          ''
        );
        break;
      default:
        break;
    }
  }

  registerHomeOperation(
    operationCode: string,
    policy: Policy,
    structure: string,
    email?: string
  ) {
    const params = this.calculateHomeParams(policy as PolicyHome);
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      '',
      email,
      policy
    );
  }

  setHomeParams(policy: PolicyHome) {
    const params = this.calculateHomeParams(policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      '',
      policy
    );
    return this.setHttpParams(body);
  }

  calculateHomeParams(policy: PolicyHome) {
    const { product, department, companyId } = policy || ({} as PolicyHome);
    let entityCode = '02';
    let group = '02';
    let productId = product || 'producto';
    const codProduct = department || '';
    if (companyId === '41') {
      entityCode = '41';
      group = this.mmobileHelper.getCustomConfigData('agrupacion_nse');
      productId = this.addressHelper.getAddressResume(policy.address)
        ? this.addressHelper.getAddressResume(policy.address)
        : productId;
    }

    if (department === '52' || department === '952' || department === '55') {
      group = '34';
    }
    if (
      department !== '73' &&
      department !== '873' &&
      department !== '39' &&
      department !== '75' &&
      department !== '84' &&
      department !== '90' &&
      department !== '39' &&
      department !== '93' &&
      department !== '875' &&
      department !== '890' &&
      department !== '893' &&
      department !== '900' &&
      department !== '901' &&
      department !== '973'
    ) {
      group = '33';
    }
    return {
      entityCode,
      group,
      productId,
      codProduct
    };
  }

  registerOthersOperation(
    operationCode: string,
    policy: Policy,
    structure: string
  ) {
    const params = this.calculateOthersOperationParams(policy);
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      '',
      '',
      policy
    );
  }

  setOtherOperationParams(policy: Policy) {
    const params = this.calculateOthersOperationParams(policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      '',
      policy
    );
    return this.setHttpParams(body);
  }

  calculateOthersOperationParams(policy: any) {
    const { product, department, aciCompany } = policy || ({} as any);
    let group = '33';
    if (
      department === '79' ||
      department === '96' ||
      department === '97' ||
      department === '63' ||
      department === '62'
    ) {
      group = department;
    }
    return {
      entityCode: aciCompany ? '22' : '02',
      group,
      productId: product || 'producto',
      codProduct: department || ''
    };
  }

  registerAutoOperation(
    operationCode: string,
    policy: PolicyCar,
    structure: string
  ) {
    const params = this.calculateAutoParams(policy);
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      '',
      '',
      policy
    );
  }

  registerChangeCarProduct(
    operationCode: string, policy: PolicyCar ) {
    const { user } = this.authMirror.authService.getState();
    const params = this.calculateAutoParams(policy);

    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      '',
      '',
      user.email
    );
  }

  registerAutoChangeHabitualAddress(
    operationCode: string, policy: PolicyCar ) {
    const { user } = this.authMirror.authService.getState();
    const params = this.calculateAutoParams(policy);

    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      '',
      '',
      user.email
    );
  }

  setAutoParams(policy: PolicyCar) {
    const params = this.calculateAutoParams(policy);
    const { user } = this.authMirror.authService.getState();
    const email = this.authMirror.authService.checkUserIsCompany()
      ? user?.legalRepresentative?.email
      : user?.email;
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      email ?? '',
      policy
    );
    return this.setHttpParams(body);
  }

  calculateAutoParams(policy: PolicyCar) {
    const { product, department } = policy || ({} as PolicyCar);
    return {
      productId: product || 'producto',
      codProduct: department || '200',
      entityCode: '01',
      group: '1'
    };
  }

  registerHealthOperation(
    operationCode: string,
    policy: PolicyHealth,
    structure = '',
    productIdName?: string,
    email: string = '',
    documentNumber: string = ''
  ) {
    const { product, productCode } = policy || ({} as PolicyHealth);
    const productId = productIdName ? productIdName : product || 'producto';
    const codProduct = productCode || '';
    const entityCode = '283';
    const group = '04';

    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      structure,
      documentNumber,
      email,
      policy
    );
  }

  setAssistanceParams(policy: PolicyHealth): HttpParams {
    const { product, productCode } = policy || ({} as PolicyHealth);
    const productId = product || 'producto';
    const codProduct = productCode || '';
    const entityCode = '283';
    const group = '04';

    const body = this.buildOperationRequestBody(
      entityCode,
      productId,
      codProduct,
      '',
      group,
      '',
      '',
      '',
      policy
    );
    return this.setHttpParams(body);
  }

  registerCopaymentCardOperation(
    operationCode: string,
    structure: string,
    isEmailSend: boolean
  ) {
    const params = this.calculateCopaymentCardParams(isEmailSend);
    this.registerFailSafe(
      params.entityCode,
      params.productId,
      '',
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email
    );
  }

  setCopaymentCardParams(isEmailSend?: boolean, structure?: string) {
    const params = this.calculateCopaymentCardParams(isEmailSend);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      '',
      '',
      params.group,
      '',
      params.documentNumber,
      params.email
    );

    return createHttpParams(
      {
        roStructure: structure,
        roEmail: body.email
      },
      this.setHttpParams(body)
    );
  }

  calculateCopaymentCardParams(isEmailSend?: boolean) {
    const { user } = this.authMirror.authService.getState();
    const product = isEmailSend ? 'N' : 'S';

    return {
      entityCode: '283',
      group: '04',
      productId: 'Papel ' + product,
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  registerAccessPaymentPlan() {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculatePaymentPlanOperationParams();
    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      PaymentPlanOperation.ACCESO,
      group,
      '',
      documentNumber,
      email
    );
  }

  registerAccessPaymentPlanAfterContracting(paymentPlans: PaymentPlan[]) {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculatePaymentPlanOperationParams();

    const structure = {
      usuario: documentNumber,
      numPlanes: paymentPlans.length,
      detalle: paymentPlans.map(p => ({
        plan: p.id,
        fecIni: p.effectDate,
        prima: p.nextFee && p.nextFee.amount,
        diaExtr: p.paymentMonthDay,
        polizas: p.policyNumbers
      }))
    };

    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      PaymentPlanOperation.ACCESO_TRAS_CONTRATACION,
      group,
      JSON.stringify(structure),
      documentNumber,
      email
    );
  }

  registerPaymentPlanOpenRedsys(extradata: any) {
    const { entityCode, productId, documentNumber, group, productCode, email } =
    this.calculatePaymentPlanOperationParams();
    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      PaymentPlanOperation.ACCESO_REDSYS,
      group,
      JSON.stringify(extradata),
      documentNumber,
      email
    );
  }

  registerUnsignedContractDownload() {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculatePaymentPlanOperationParams();

    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      PaymentPlanOperation.DESCARGA_CONTRATO_SIN_FIRMAR,
      group,
      '',
      documentNumber,
      email
    );
  }

  registerAccessPaymentPlanList(paymentPlans: PaymentPlan[]) {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculatePaymentPlanOperationParams();
    const structure = {
      usuario: documentNumber,
      numPlanes: paymentPlans.length,
      detalle: paymentPlans.map(p => ({
        plan: p.id,
        fecIni: p.effectDate,
        prima: p.nextFee && p.nextFee.amount,
        diaExtr: p.paymentMonthDay,
        polizas: p.policyNumbers
      }))
    };

    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      PaymentPlanOperation.LISTADO,
      group,
      JSON.stringify(structure),
      documentNumber,
      email
    );
  }

  setPaymentPlanOperationParams(custom: any = {}): HttpParams {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculatePaymentPlanOperationParams();
    const body = this.buildOperationRequestBody(
      entityCode,
      custom.productId || productId,
      productCode,
      '',
      group,
      '',
      documentNumber,
      email
    );

    return this.setHttpParams(body);
  }

  private calculatePaymentPlanOperationParams() {
    const { user } = this.authMirror.authService.getState();

    return {
      entityCode: '00',
      productId: 'Cuota',
      group: '0',
      productCode: '',
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  registerNetworkOperationParams(user: User) {
    const params = { entityCode: '00', group: '0' };
    const body = this.buildOperationRequestBody(
      params.entityCode,
      '',
      '',
      '',
      params.group,
      '',
      '',
      user.email
    );
    return this.setHttpParams(body);
  }

  registerAccessHelpAsk() {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculateHelpOperationParams();
    const structure = { documentNumber };
    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      HelpOperation.ACCESO,
      group,
      JSON.stringify(structure),
      documentNumber,
      email
    );
  }

  registerHelpAskOK() {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculateHelpOperationParams();
    const structure = { documentNumber };
    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      HelpOperation.PREGUNTA_OK,
      group,
      JSON.stringify(structure),
      documentNumber,
      email
    );
  }

  registerHelpAskKO({ question, error }) {
    const { entityCode, productId, documentNumber, group, productCode, email } =
      this.calculateHelpOperationParams();
    const structure = { documentNumber, question, error };
    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      HelpOperation.PREGUNTA_KO,
      group,
      JSON.stringify(structure),
      documentNumber,
      email
    );
  }

  private calculateHelpOperationParams() {
    const { user } = this.authMirror.authService.getState();

    return {
      entityCode: '00',
      productId: 'Contacto',
      group: '0',
      productCode: '',
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  registerLifeOperation(
    operationCode: string,
    policy: PolicyLife | PolicySaving | PolicyCollectiveLife,
    structure: string,
    productId?: string,
    documentNumber: string = ''
  ) {
    const params = this.calculateLifeOperationParams(policy, { productId });
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      documentNumber,
      params.email,
      policy
    );
  }

  setLifeOperationParams(
    policy: PolicyLife | PolicySaving | PolicyCollectiveLife,
    payload?: { productId: string }
  ) {
    const params = this.calculateLifeOperationParams(policy, payload);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      '',
      policy
    );
    return this.setHttpParams(body);
  }

  setCollectiveLifeOperationParams(
    policy: PolicyLife | PolicySaving | PolicyCollectiveLife,
    payload?: { productId: string }
  ) {
    const params = this.calculateLifeOperationParams(policy, payload);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      params.email,
      policy
    );
    return this.setHttpParams(body);
  }

  calculateLifeOperationParams(
    policy: PolicyLife | PolicySaving | PolicyCollectiveLife,
    payload?: { productId: string }
  ) {
    let productId = '';
    const { user } = this.authMirror.authService.getState();
    if (policy && !payload?.productId) {
      switch (policy.policyBaseType) {
        case 'saving':
          productId = 'AHORRO';
          break;
        case 'life':
          productId = 'RIESGO';
          break;
        case 'collective-life':
          productId = 'VIDA COLECTIVOS';
          break;
        default:
          break;
      }
      productId += policy?.modalityId ? ` - ${policy.modalityId}` : '';
      productId += policy?.product ? ` - ${policy.product}` : ' - producto';
    } else {
      productId = payload?.productId ?? '';
    }
    return {
      entityCode: '03',
      group: policy?.policyBaseType === 'collective-life' ? '15' : '9',
      productId,
      codProduct: policy?.modalityId || '',
      email: user?.email || ''
    };
  }

  registerAccessBiometric(operationCode: string, documentNumber?: string, access?: string) {
    if (!this.platform.is('cordova')) {
      return this.log.e(
        this,
        'Cancelling: operation register not supported on Web'
      );
    }

    const entityCode = '00';
    const group = '0';
    const codProduct = '';
    const platform = this.platform.is('android') ? 'Android' : 'IOS';
    const productId =  access ? `${platform} - ACCESO ${access}` : platform;
    const structure = {
      versionApp: Environment.config().version,
      versionOS: this.device.version,
      deviceModel: [this.device.manufacturer, this.device.model].join(' ')
    };
    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      JSON.stringify(structure),
      documentNumber
    );
  }

  registerAccessPasswordOrHome(
    operationCode: string,
    documentNumber?: string,
    email?: string,
    access?: string,
    policies?: ProductCard[] | ClientAreaConfigurableCard[]
  ) {
    let platform = '';
    let productIdOrigin = '';
    if (this.platform.is('cordova')) {
      platform = this.platform.is('android') ? 'Android -' : 'IOS -';
      productIdOrigin = this.platform.is('android') ? 'Android' : 'IOS';
    } else {
      productIdOrigin = 'Web';
    }
    const productId = access ? `${platform} ACCESO ${access}` : productIdOrigin;
    const entityCode = '00';
    const group = '0';
    const codProduct = '';
    let structure = '';
    if (policies) {
      structure = JSON.stringify(this.getOverallPositionOperationStructure(policies));
    }
    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      structure,
      documentNumber,
      email
    );
  }

  private getOverallPositionOperationStructure(policies: any[]) {
    const structure: {
      detail: string[];
      versionApp?: string;
      versionOS?: string;
      deviceModel?: string;
      userAgent?: string;
      operatingSystem?: string;
    } = { detail: [] };
    structure.versionApp = Environment.config().version;
    if (this.platform.is('cordova')) {
      structure.versionOS = this.device.version;
      structure.deviceModel = [this.device.manufacturer, this.device.model].join(' ');
    } else {
      structure.userAgent = getUserAgent();
      structure.operatingSystem = navigator.platform;
    }
    if (policies[0]?.cardConfig?.cardType) {
      policies.forEach((card: ClientAreaConfigurableCard) => {
        const groupingName = card.cardConfig?.groupingName;
        card.groupedPoliciesLists?.policyBaseEntries.forEach(
          (policyBaseEntry: PolicyBaseEntry) => {
            const policiesStructure = (policyBaseEntry.policiesList || []).map(
              (policy: any) =>
                `${groupingName || policy.policyBaseType} - ${policy.policyNumber} Entidad: ${
                  policy.companyId || ''
                } Producto: ${policy.department || policy.productCode || policy.product || ''}`
            );
            structure.detail.push(...policiesStructure);
          }
        );
      });
    } else {
      structure.detail = policies.map(
        policy => {
          if (policy.policies?.length > 0) {
            return (policy.policies || []).map(groupedPolicy =>
              `${policy.cardTypeStringRo} - ${groupedPolicy.policyNumber} Entidad: ${groupedPolicy.companyId || ''
              } Producto: ${groupedPolicy.department || groupedPolicy.productCode || groupedPolicy.product || ''}`
            );
          } else {
            return `${policy.cardTypeStringRo} - ${policy.policy?.policyNumber} Entidad: ${policy.policy?.companyId || ''
              } Producto: ${policy.policy?.department || policy.policy?.productCode || policy.policy?.product || ''}`;
          }
        }
      );
    }
    return structure;
  }

  registerAllPoliciesListsEmpty(operationCode: string) {
    const codProduct = '';
    const entityCode = '00';
    const group = '0';
    const productId = this.authMirror.authService.getConcatenatedUserIds();

    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      ''
    );
  }

  registerReducedHomeAccess(operationCode: string, policiesNumber: number, maxPrefetchPolicies: number, documentNumber?: string) {
    const codProduct = '';
    const entityCode = '00';
    const group = '0';
    const productId = `Máximo ${maxPrefetchPolicies} - Pólizas: ${policiesNumber}`;

    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      '',
      documentNumber
    );
  }

  registerAllBranchesPoliciesError(
    errors: { type: PolicyBaseType; errorDetail: any }[]
  ) {
    const productId = 'producto';
    const entityCode = '00';
    const group = '0';
    const codProduct = '';
    const structure = errors
      .map(({ errorDetail }) => jsonStringify(errorDetail))
      .join();

    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      TechOperation.OVERALL_POSITION_ALL_BRANCHES_ERROR,
      group,
      structure
    );
  }

  registerBranchPoliciesError(
    errors: { type: PolicyBaseType; errorDetail: any }[]
  ) {
    const entityCode = '00';
    const group = '0';
    const codProduct = '';
    const errorsToRegisterWithDuplicates = errors.map(
      ({ type, errorDetail }) => ({
        productId: PolicyBaseTypeToProductId[type],
        structure: jsonStringify(errorDetail),
      })
    );
    const errorsToRegister = [
      ...new Map(
        errorsToRegisterWithDuplicates.map(error => [error.productId, error])
      ).values()
    ];

    errorsToRegister.forEach(({ productId, structure }) => {
      return this.registerFailSafe(
        entityCode,
        productId,
        codProduct,
        TechOperation.OVERALL_POSITION_SINGLE_BRANCH_ERROR,
        group,
        structure
      );
    });
  }

  setLoginOperationParams(type: AuthType, documentNumber: string, acccess?: string): HttpParams {
    const { entityCode, productId, group, productCode, structure } = this.calculateLoginParams(acccess);
    const body = this.buildOperationRequestBody(
      entityCode,
      productId,
      productCode,
      '',
      group,
      JSON.stringify(structure)
    );
    return createHttpParams(
      {
        roUserCode: body.userCode,
        roStructure: body.structure,
        roDocumentNumber: this.authMirror.authService.checkUserIsParticular() ? documentNumber : body.documentNumber
      },
      this.setHttpParams(body)
    ).append('loginType', type.toString());
  }

  private calculateLoginParams(access?: string) {
    const platform = !this.platform.is('cordova')
      ? ''
      : this.platform.is('android')
      ? 'Android'
      : 'IOS';
    const productId = access ? `${platform} - ACCESO ${access}` : platform;
    const structure = !this.platform.is('cordova')
      ? {}
      : {
          versionApp: Environment.config().version,
          versionOS: this.device.version,
          deviceModel: [this.device.manufacturer, this.device.model].join(' ')
        };

    return {
      entityCode: '00',
      group: '0',
      productCode: '',
      productId,
      structure
    };
  }

  registerPensionOperation(
    operationCode: string,
    policy: Policy,
    structure: string,
    productId?: string
  ) {
    const pensionPolicy = (policy || {}) as PolicyPension;
    const params = this.calculatePensionParams(pensionPolicy);
    return this.registerFailSafe(
      params.entityCode,
      productId || params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email,
      policy
    );
  }

  setPensionPoliciesOperationParams(policyNumber: string) {
    const policy = (this.policiesCtx.getPolicy(policyNumber) ||
      {}) as PolicyPension;
    const params = this.calculatePensionParams(policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      params.documentNumber,
      params.email
    );
    return this.setHttpParams(body);
  }

  calculatePensionParams({
    belongsToLifeCycle,
    productDescription,
    productCode,
    id,
    lifeCycleCode
  }: PolicyPension) {
    const { user } = this.authMirror.authService.getState();

    return {
      entityCode: '03',
      group: belongsToLifeCycle || !id ? '12' : id,
      productId: belongsToLifeCycle ? lifeCycleCode : productDescription,
      codProduct: belongsToLifeCycle ? '' : productCode,
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  registerInvestmentOperation(
    operationCode: string,
    policy: Policy,
    structure: string,
    productIdName?: string,
    email: string = '',
    documentNumber: string = ''
  ) {
    const investmentPolicy = policy as PolicyInvestment;
    let productId = 'producto';
    const entityCode = '03';
    const group = '11';
    let codProduct = '';
    if (productIdName){
      productId = productIdName;
    } else if (investmentPolicy) {
      if (investmentPolicy.productId) {
        productId = investmentPolicy.productId;
      }
      if (investmentPolicy.productCode) {
        codProduct = investmentPolicy.id;
      }
    } else {
      productId = 'Mi cartera de inversión';
    }
    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      structure,
      documentNumber,
      email,
      policy
    );
  }

  getTeCuidamosRegistrationFormRGPDUERoData(tcData: TeCuidamosRegister): any {
    if (!tcData) {
      return {};
    }
    return {
      nif: tcData.documentNumber,
      nombre: tcData.name,
      apellido1: tcData.surname1,
      apellido2: tcData.surname2,
      fecha_nacimiento: tcData.date,
      sexo: tcData.sex,
      estado: tcData.status,
      tipo_via: tcData.streetType,
      nombre_calle: tcData.street,
      numero: tcData.number,
      complmento: tcData.additionalAddressInfo,
      codigoPostal: tcData.postalCode,
      localidad: tcData.location,
      provincia: tcData.province,
      email: tcData.email,
      movil: tcData.mobile,
      telefono: tcData.phone
    };
  }

  registerTeCuidamosOperation(operationCode: string, structure: string) {
    const productId = 'teCuidamos';
    let entityCode = '77';
    let group = '77';
    const codProduct = '';
    if (
      operationCode === TeCuidamosOperation.ACCESS ||
      operationCode === TeCuidamosOperation.ERROR_ACCESS
    ) {
      entityCode = '00';
      group = '0';
    }
    return this.registerFailSafe(
      entityCode,
      productId,
      codProduct,
      operationCode,
      group,
      structure
    );
  }

  registerTeCuidamosSsoOperation(product: string, operationCode: string, structure?: string) {
    const params = this.registerTeCuidamosParams(product);
    return this.registerFailSafe(
      params.roEntityCode,
      params.roProductId,
      '',
      operationCode,
      params.roGroupingCode,
      JSON.stringify(structure) || '',
      params.roDocumentNumber,
      params.roEmail
    );
  }

  registerTeCuidamosParams(product: string) {
    const { user } = this.authMirror.authService.getState();
    return {
      roEntityCode: '00',
      roGroupingCode: '0',
      roProductId: product,
      roOrigin: `${this.getOrigin()}`,
      roEmail: (user && user.email) || '',
      roDocumentNumber: (user && user.documentNumber) || ''
    };
  }

  calculateTeCuidamosParams() {
    return {
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.getOrigin()}`,
      roProductId: this.ctx.isIOSApp ? 'IOS' : this.ctx.isApp ? 'Android' : 'Web'
    };
  }

  registerRGPDUE(
    entityCode: string,
    group: string,
    operationCode: string,
    product: string,
    structure: string,
    documentNumber?: string
  ) {
    return this.register(
      entityCode,
      product,
      '',
      operationCode,
      group,
      structure,
      documentNumber
    );
  }

  registerRGPDUETech(
    operationCode: string,
    structure: string,
    product: string,
    documentNumber?: string
  ) {
    const orEntity = '00';
    const orGroup = '0';
    return this.registerRGPDUE(
      orEntity,
      orGroup,
      operationCode,
      product,
      structure,
      documentNumber
    );
  }

  registerRGPDUEClub(operationCode: string, structure: string, product: string) {
    const orEntity = '77';
    const orGroup = '77';
    return this.registerRGPDUE(
      orEntity,
      orGroup,
      operationCode,
      product,
      structure
    );
  }

  registerRGPDUERights(params: {
    operationCode: RgpdueRights;
    productId?: string;
    structure?: string;
  }) {
    const entityCode = '00';
    const group = '0';
    const productId = params.productId ? params.productId : '';
    const structure = params.structure ? params.structure : '';
    this.registerFailSafe(
      entityCode,
      productId,
      '',
      params.operationCode,
      group,
      structure
    );
  }

  setRGPDUERightsParams(rightSelectionData: RGPDUERightSelectionData) {
    const params = this.calculateRGPDUERightsOperationParams();
    const { rightSelectedOption } = rightSelectionData;
    let productId;
    switch (rightSelectedOption) {
      case RGPDUERightSelectOptions.OPPOSITION:
        productId = 'Oposición';
        break;
      case RGPDUERightSelectOptions.RECEIVE_INFO:
        productId = 'Revocación';
        break;
      case RGPDUERightSelectOptions.OTHER_RIGHT:
        productId = 'Otros';
        break;
      case RGPDUERightSelectOptions.PORTABILITY:
        productId = 'Portabilidad';
        break;
      default:
        productId = '';
        break;
    }
    const body = this.buildOperationRequestBody(
      params.entityCode,
      productId,
      params.productCode,
      '',
      params.group,
      ''
    );
    return this.setHttpParams(body);
  }

  private calculateRGPDUERightsOperationParams() {
    return {
      entityCode: '00',
      group: '0',
      productCode: ''
    };
  }

  registerPrivatePaymentOperation(
    type: string,
    productCode: string,
    operationCode: PaymentOperation,
    structure: string,
    documentNumber = '',
    email = ''
  ): void {
    let entity: string;
    let group: string;
    let productId = '';
    const configurableType = this.getReceiptPaymentDepartmentData(type);
    if (
      configurableType &&
      configurableType.roEntity &&
      configurableType.roGroup
    ) {
      entity = configurableType.roEntity;
      group = configurableType.roGroup;
      productId = configurableType.departmentName;
    } else {
      if (this.authMirror.authService.checkUserIsParticular()) {
      entity = '02';
      group = '33';
    }
      if (this.authMirror.authService.checkUserIsCompany()) {
        entity = '00';
        group = '0';
      }
    }
    this.registerFailSafe(
      entity,
      productId,
      productCode,
      operationCode,
      group,
      structure,
      documentNumber,
      email
    );
  }

  setPaymentOperationParams(
    receiptType: string,
    productCode: string,
    risk: string,
    documentNumber: string,
    email?: string,
    paymentType?: TpvPaymentType
  ): any {
    const params = this.calculatePaymentOperationParams(
      receiptType,
      productCode,
    );
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.productCode,
      '',
      params.group,
      '',
      documentNumber
    );

    let roOperationCodePrefix;
    if (this.authMirror.authService.checkUserIsCompany()) {
      roOperationCodePrefix = '158';
    } else {
      roOperationCodePrefix = '88';
    }

    if (paymentType === 'bizum') {
      roOperationCodePrefix = '';
    }

    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber,
        roEmail: email || '',
        roOperationCodePrefix,
        roStructure: risk ? JSON.stringify({ risk }) : ''
      },
      this.setHttpParams(body)
    );
  }

  private calculatePaymentOperationParams(
    receiptType: string,
    prodCode: string,
  ) {
    let entity: string;
    let groupCode: string;
    let prodId: string;
    const configurableType = this.getReceiptPaymentDepartmentData(receiptType);
    if (
      configurableType &&
      configurableType.roEntity &&
      configurableType.roGroup
    ) {
      entity = configurableType.roEntity;
      groupCode = configurableType.roGroup;
      prodId = configurableType.departmentName;
    } else {
      if (this.authMirror.authService.checkUserIsParticular()) {
      entity = '02';
      groupCode = '33';
    }
      if (this.authMirror.authService.checkUserIsCompany()) {
        entity = '00';
        groupCode = '0';
      }
    }
    return {
      entityCode: entity,
      productId: prodId,
      productCode: prodCode,
      group: groupCode
    };
  }

  registerSuperbonusAccessOperation(policy: PolicyCar) {
    const superbonusStatus =
      SuperbonusStatus[policy.superbonusData.statusCode] || '';
    const structure = { superbonusStatus };
    this.registerSuperbonusOperation(
      policy,
      SuperbonusOperation.ACCESO_SUPERBONUS,
      structure
    );
  }

  registerSuperbonusOperation(
    policy: PolicyCar,
    operationCode: SuperbonusOperation,
    structure: {}
  ): void {
    const orStructure = this.getSuperbonusStructure(policy, structure);
    const params = this.calculateSuperbonusOperationParams(policy);
    this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.productCode,
      operationCode,
      params.group,
      orStructure,
      params.documentNumber,
      params.email
    );
  }

  setSuperbonusOperationParams(
    policy: PolicyCar,
    selectedPaymentMethod: SuperbonusOfferType
  ): HttpParams {
    const orStructure = this.getSuperbonusStructure(policy);
    const params = this.calculateSuperbonusOperationParams(
      policy,
      selectedPaymentMethod
    );
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.productCode,
      '',
      params.group,
      orStructure,
      params.documentNumber,
      params.email
    );

    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber,
        roEmail: body.email,
        roStructure: body.structure
      },
      this.setHttpParams(body)
    );
  }

  private calculateSuperbonusOperationParams(
    policy: PolicyCar,
    selectedPaymentMethod?: SuperbonusOfferType
  ): any {
    const entity = '01';
    const groupCode = '1';
    let prodId = '';
    const { user } = this.authMirror.authService.getState();
    if (selectedPaymentMethod) {
      prodId = selectedPaymentMethod === 'TR' ? 'TREBOLES' : 'TRANSFERENCIA';
    } else {
      prodId = this.getVehicleDescription(policy);
    }
    return {
      entityCode: entity,
      productId: prodId,
      group: groupCode,
      productCode: policy && policy.department ? policy.department : '',
      email: user && user.email ? user.email : '',
      documentNumber: user && user.documentNumber ? user.documentNumber : ''
    };
  }

  private getSuperbonusStructure(policy: PolicyCar, structure = {}): string {
    const orStructure = {
      vehicle: this.getVehicleDescription(policy),
      policyNumber: policy && policy.policyNumber ? policy.policyNumber : '',
      ...structure
    };
    return JSON.stringify(orStructure);
  }

  private getVehicleDescription(policy: PolicyCar): string {
    let vehicleDescription = '';
    if (policy) {
      if (policy.plateNumber) {
        vehicleDescription += policy.plateNumber + ' ';
      }
      if (policy.vehicle) {
        vehicleDescription += policy.vehicle;
      }
    }
    return vehicleDescription.trim();
  }

  registerFamiliesOperation(
    operationCode: FamiliesOperation,
    orStructure: string,
    isCreator?: boolean,
    operationOrigin?: string
  ): void {
    let productId = '';
    if (isCreator) {
      productId = 'Creador';
    } else if (isCreator !== null) {
      productId = 'Familiar';
    }
    if (operationCode === FamiliesOperation.LEAVE_PROCESS && operationOrigin) {
      productId += operationOrigin;
    }
    const params = this.calculateFamiliesOperationParams();
    this.registerFailSafe(
      params.entityCode,
      productId,
      params.productCode,
      operationCode,
      params.group,
      orStructure,
      params.documentNumber,
      params.email
    );
  }

  setFamiliesOperationParams(userFamilyLogin): HttpParams {
    const params = this.calculateFamiliesOperationParams();
    const productId = !userFamilyLogin
      ? ''
      : userFamilyLogin && userFamilyLogin.creator
      ? 'Creador'
      : 'Familiar';
    const body = this.buildOperationRequestBody(
      params.entityCode,
      productId,
      params.productCode,
      '',
      params.group,
      '',
      params.documentNumber,
      params.email
    );

    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber,
        roEmail: body.email
      },
      this.setHttpParams(body)
    );
  }

  private calculateFamiliesOperationParams(): any {
    const { user } = this.authMirror.authService.getState();
    return {
      entityCode: '00',
      group: '0',
      productCode: '',
      email: user && user.email ? user.email : '',
      documentNumber: user && user.documentNumber ? user.documentNumber : ''
    };
  }

  getTranslatedFamiliesStructure(family: Family, structureName: string): any {
    const structure = this.getFamiliesStructure(family);
    const translatedStructure = {};
    translatedStructure[`${structureName}`] = {
      Parentesco: structure.relationshipDescription,
      'Documento identificativo': structure.documentNumber,
      Nombre: structure.name,
      'Primer apellido': structure.surname1,
      'Segundo apellido': structure.surname2
    };
    return translatedStructure;
  }

  private getFamiliesStructure(family: Family): any {
    const { user } = this.authMirror.authService.getState();
    return {
      relationshipDescription:
        family && family.self ? family.self.relationshipDescription : '',
      documentNumber: user ? user.documentNumber : '',
      name: user ? user.name : '',
      surname1: user ? user.surname1 : '',
      surname2: user ? user.surname2 : ''
    };
  }

  private calculatePrivatePatrimonialsOperationParams(
    policy: Policy,
    sinisterType: string
  ): any {
    const homePolicy = policy as PolicyHome;
    let prodId;
    const { user } = this.authMirror.authService.getState();
    if (sinisterType) {
      prodId = this.mmobileHelper.getCustomConfigData(
        'availableSinistersData',
        sinisterType,
        'name'
      );
    } else {
      prodId = 'Inicio';
    }
    const entityCod = padZeros(homePolicy.companyId, 2);
    let groupCode = '';
    const policyProduct = padZeros(homePolicy.department, 3);
    const productCode = homePolicy.department;
    const productsGrouping2 = ['039', '073', '075', '090', '093', '900', '973'];
    const productsGrouping33 = ['046', '096', '946', '078', '902', '992', '908', '904', '905', '046', '096', '946', '869',
      '076', '091', '036', '037', '038', '077', '080', '027', '903', '023', '030', '063', '062', '074', '874', '878' ];
    if (productsGrouping33.find(product => product === policyProduct)) {
      groupCode = '33';
    }
    if (productsGrouping2.find(product => product === policyProduct)) {
      groupCode = '2';
    }
    return {
      entityCode: entityCod,
      productId: prodId,
      codProduct: productCode,
      group: groupCode,
      documentNumber: user.documentNumber
    };
  }

  private calculatePublicPatrimonialOperationParams(
    sinisterType: string,
    policy: Policy,
    productId?: string,
    docNumber?: string
  ) {
    const homePolicy = policy as PolicyHome;
    let prodId = '';
    if (productId) {
      prodId = productId;
    } else {
      if (sinisterType) {
        prodId = this.mmobileHelper.getCustomConfigData(
          'availableSinistersData',
          sinisterType,
          'name'
        );
      } else {
        prodId = '';
      }
    }
    return {
      entityCode: 58,
      group: 65,
      productId: prodId,
      codProduct: policy && homePolicy.department ? homePolicy.department : '',
      documentNumber:
        docNumber ||
        this.patrimonialContext.getPatrimonialsClientInfo().documentNumber
    };
  }

  registerPatrimonialsOperation(
    operationCode: string,
    policy: Policy,
    sinisterType: string,
    structure: string,
    isPrivateArea: boolean,
    productId?: string,
    docNumber?: string
  ) {
    const params = isPrivateArea
      ? this.calculatePrivatePatrimonialsOperationParams(policy, sinisterType)
      : this.calculatePublicPatrimonialOperationParams(
          sinisterType,
          policy,
          productId,
          docNumber
        );
    const opCode = isPrivateArea ? '8' + operationCode : operationCode;
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      opCode,
      params.group,
      structure,
      params.documentNumber,
      '',
      policy
    );
  }

  setPatrimonialsOperationParams(
    policy: Policy,
    sinisterType: string,
    isPrivateArea: boolean
  ): HttpParams {
    const params = isPrivateArea
      ? this.calculatePrivatePatrimonialsOperationParams(policy, sinisterType)
      : this.calculatePublicPatrimonialOperationParams(sinisterType, policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      params.documentNumber,
      '',
      policy
    );
    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber
      },
      this.setHttpParams(body)
    );
  }

  // PWA Patrimonals - OneTrust RPGDUE

  registerRGPDUEPWAPatrimonials(documentNumber: string) {
    const oneTrustROProduct = 'Parte Patrimoniales';
    const privacyPolicyUrl =
      'https://www.mapfre.es/privacidad';
    const structure = JSON.stringify({
      nif: documentNumber,
      'URL politica de privacidad': privacyPolicyUrl,
      'Aceptacion de privacidad': 'SI'
    });
    const entity = '58';
    const groupingCode = '65';
    const opCode = RGPDUEPWAPatrimonials.ACEPTACION_CONDICIONES;

    this.registerFailSafe(
      entity,
      oneTrustROProduct,
      null,
      opCode,
      groupingCode,
      structure,
      documentNumber
    );
  }

  // Car Sinister Without Contrary

  setCarSinisterWithoutContraryOperationParams(policy: PolicyCar) {
    const params =
      this.calculateCarSinisterWithoutContraryOperationParams(policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      '',
      '',
      policy
    );
    return this.setHttpParams(body);
  }

  private calculateCarSinisterWithoutContraryOperationParams(
    policy: PolicyCar
  ) {
    return {
      entityCode: '01',
      productId: `Matrícula: ${policy.plateNumber}`,
      codProduct: policy.department || '',
      group: '1'
    };
  }

  registerCarSinisterConsultationOperation(
    operationCode: CarSinisterConsultationOperation,
    plateNumber: string,
    structure: string,
    documentNumber: string,
    email: string
  ) {
    const params =
      this.calculateCarSinisterConsultationOperationParams(plateNumber);
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      documentNumber,
      email
    );
  }

  setCarSinisterConsultationOperationParams(
    plateNumber: string,
    documentNumber: string,
    email: string,
    structure?: string
  ) {
    const params =
      this.calculateCarSinisterConsultationOperationParams(plateNumber);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      params.codProduct,
      '',
      params.group,
      '',
      documentNumber,
      email
    );

    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber,
        roStructure: structure ? structure : '',
        roEmail: body.email
      },
      this.setHttpParams(body)
    );
  }

  private calculateCarSinisterConsultationOperationParams(plateNumber: string) {
    return {
      entityCode: this.authMirror.authService.checkUserIsParticular() ? '01' : '41',
      productId: `Matrícula: ${plateNumber}`,
      codProduct: '',
      group: '1'
    };
  }

  registerPhoneCallRoadAssistanceOperation(product: string) {
    const params = this.calculateRoadAssistanceOperationParams(
      product,
      '',
      RoadAssistanceOperation.PHONE_CALL,
      '1',
      ''
    );
    return this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.productCode,
      params.operation,
      params.group,
      params.structure
    );
  }

  private calculateRoadAssistanceOperationParams(
    paramProductId: string,
    paramProductCode: string,
    paramOperation: string,
    paramGroup: string,
    paramStructure: string
  ) {
    const entityCode = '01';
    const productId = paramProductId;
    const productCode = paramProductCode;
    const operation = paramOperation;
    const group = paramGroup;
    const structure = paramStructure;
    return {
      entityCode,
      productId,
      productCode,
      operation,
      group,
      structure
    };
  }

  setRoadAssistanceParams(plateNumber: string, documentNumber: string) {
    const params = this.calculateRoadAssistanceOperationParams(
      plateNumber,
      '',
      '',
      '1',
      ''
    );
    const body = this.buildOperationRequestBody(
      params.entityCode,
      params.productId,
      '',
      '',
      params.group,
      '',
      documentNumber
    );
    let httpParams = this.setHttpParams(body);
    httpParams = httpParams.set('roOperationCodePrefix', '88');
    httpParams = httpParams.set('roDocumentNumber', body.documentNumber);
    return httpParams;
  }

  registerAccessRatingMarket(stars: number, comment: string) {
    const { entityCode, group } = this.calculateRatingParams();
    const productId = `${stars.toString()} estrellas`;
    const operation = '88652';
    const structure = {
      'Número de estrellas': stars,
      Comentario: comment || 'N/A'
    };
    return this.registerFailSafe(
      entityCode,
      productId,
      '',
      operation,
      group,
      JSON.stringify(structure)
    );
  }

  setRatingParams(stars: number, comment: string) {
    const { entityCode, group } = this.calculateRatingParams();
    const productId = JSON.stringify(`${stars} estrellas`);
    const structure = {
      'Número de estrellas': stars,
      Comentario: comment || 'N/A'
    };
    const body = this.buildOperationRequestBody(
      entityCode,
      productId,
      '',
      '',
      group,
      ''
    );

    return createHttpParams(
      {
        roDocumentNumber: body.documentNumber,
        roStructure: JSON.stringify(structure)
      },
      this.setHttpParams(body)
    );
  }

  private calculateRatingParams() {
    return {
      entityCode: '00',
      group: '0'
    };
  }

  registerBankAccountChangeOperation(
    policy: EnabledPolicyToChangeBankAccount,
    operationCode: string,
    productId = 'Cambio CCC',
    structure = ''
  ) {
    const params = this.calculateBankDataManagementParams(policy);
    return this.registerFailSafe(
      params.entityCode,
      productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email,
      policy
    );
  }

  setBankDataManagementParams(
    policy: Policy,
    productId: string,
    structure = ''
  ) {
    const params = this.calculateBankDataManagementParams(policy);
    const body = this.buildOperationRequestBody(
      params.entityCode,
      productId,
      params.codProduct,
      '',
      params.group,
      structure,
      params.documentNumber,
      params.email,
      policy
    );
    return this.setHttpParams(body);
  }

  private calculateBankDataManagementParams(policy: Policy) {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, group, codProduct } =
      this.getParamsByPolicyType(policy);
    return {
      entityCode,
      group,
      documentNumber: user.documentNumber,
      email: user.email,
      codProduct
    };
  }

  registerSigningKeyOperations(
    operationCode: string,
    productId: string,
    structure: string
  ) {
    const params = this.calculateSigningKeyParams();
    return this.registerFailSafe(
      params.entityCode,
      productId,
      '',
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email
    );
  }

  setSigningKeyParams(productId: string) {
    const params = this.calculateSigningKeyParams();
    const body = this.buildOperationRequestBody(
      params.entityCode,
      productId,
      '',
      '',
      params.group,
      '',
      params.documentNumber,
      params.email
    );
    return createHttpParams(
      {
        roEmail: body.email
      },
      this.setHttpParams(body)
    );
  }

  private calculateSigningKeyParams() {
    const { user } = this.authMirror.authService.getState();
    const entityCode = '00';
    const group = '0';
    return {
      entityCode,
      group,
      email: user.email,
      documentNumber: user.documentNumber
    };
  }

  registerProfileOperations(
    operationCode: string,
    productId: string,
    structure: string
  ) {
    const params = this.calculateProfileParams();
    return this.registerFailSafe(
      params.entityCode,
      productId,
      '',
      operationCode,
      params.group,
      structure
    );
  }

  setProfileParams(productId: string, structure: string) {
    const params = this.calculateProfileParams();
    const body = this.buildOperationRequestBody(
      params.entityCode,
      productId,
      '',
      '',
      params.group,
      ''
    );

    let httpParams = this.setHttpParams(body);
    if (structure !== '') {
      httpParams = httpParams.set('roStructure', structure);
    }
    return httpParams;
  }

  registerForgetPasswordOperation(
    operationCode: string,
    origin: ForgotPasswordOrigin,
    documentNumber: string,
    structureDevice = ''
  ) {
    const entityCode = '00';
    const group = '0';
    const productId = '';
    const productCode = '';
    const structure =
      origin === ForgotPasswordOrigin.FROM_LOGIN
        ? 'Login'
        : 'Olvido de contraseña';

    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      operationCode,
      group,
      `${structure}. ${structureDevice}`,
      documentNumber
    );
  }

  registerVehiclePaymentChangeOperations(
    policy: Policy,
    operationCode: string,
    structure: string
  ) {
    const params = this.getVehiclePaymentChangeROParams(policy);

    const register = this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email
    );

    return register;
  }

  private getVehiclePaymentChangeROParams(policy: Policy) {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, group, codProduct } = this.getParamsByPolicyType(policy);
    return {
      entityCode,
      codProduct,
      productId: policy['product'] || 'product',
      group,
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  getROQueryParams(policy: Policy): string {
    const origin = this.getOrigin();
    const params = this.getParamsByPolicyType(policy);
    const entityCode = params.entityCode;
    const groupCode = params.group;

    return `?roEntityCode=${entityCode}&roGroupingCode=${groupCode}&roOrigin=${origin}&roOperationCodePrefix=8`;
  }

  getUrlQueryParams(policy: Policy): HttpParams {
    const origin = this.getOrigin();
    const params = this.getParamsByPolicyType(policy);
    const entityCode = params.entityCode;
    const groupCode = params.group;
    const productId = params.productId;
    const productCode = params.codProduct;

    return new HttpParams()
    .set('roEntityCode', entityCode)
    .set('roGroupingCode', groupCode)
    .set('roOrigin', origin.toString())
    .set('roProductId', productId)
    .set('roProductCode', productCode)
    .set('roOperationCodePrefix', '8');
  }

  getContactUrlQueryParams(policy: Policy, productId: string): HttpParams {
    const origin = this.getOrigin();
    const { user } = this.authMirror.authService.getState();
    const params = user ? this.getParamsByPolicyType(policy) : null;

    const { entityCode, groupCode, productCode, email, documentNumber} = {
      entityCode: '00',
      groupCode: '0',
      productCode: params ? params.codProduct : '',
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };

    return new HttpParams()
    .set('roEntityCode', entityCode)
    .set('roGroupingCode', groupCode)
    .set('roOrigin', origin.toString())
    .set('roProductId', productId)
    .set('roOperationCodePrefix', '8')
    .set('roDocumentNumber', documentNumber);
  }

  getSendRefundInformationUrlQueryParams(policy: PolicyHealth, productIdName?: string): HttpParams {
    const origin = this.getOrigin();
    const { user } = this.authMirror.authService.getState();
    const params = this.getParamsByPolicyType(policy);

    const { entityCode, groupCode, userCode, email, documentNumber} = {
      entityCode: params.entityCode,
      groupCode: params.group,
      userCode: (user && user.userCode) || '',
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };

    const { product } = policy || ({} as PolicyHealth);
    const productId = productIdName ? productIdName : product || 'producto';

    return new HttpParams()
    .set('roEntityCode', entityCode)
    .set('roGroupingCode', groupCode)
    .set('roOrigin', origin.toString())
    .set('roProductId', productId)
    .set('roOperationCodePrefix', '8')
    .set('roUserCode', userCode)
    .set('roDocumentNumber', documentNumber)
    .set('roEmail', email);
  }

  regiesterContact(operationCode: string, productId: string, document?: string) {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, documentNumber, group, productCode, email } = {
      entityCode: '00',
      group: '0',
      productCode: '',
      email: (user && user.email) || '',
      documentNumber: user ? user.documentNumber : document ? document : ''
    };

    return this.registerFailSafe(
      entityCode,
      productId,
      productCode,
      operationCode,
      group,
      '',
      documentNumber,
      email
    );
  }

  public getRoadAssistanceUrlQueryParams(policy?: PolicyCar) {
    const origin = this.getOrigin();

    return new HttpParams()
    .set('roEntityCode', '01')
    .set('roGroupingCode', '1')
    .set('roOrigin', origin.toString())
    .set('roOperationCodePrefix', '8')
    .set('companyId', policy.companyId);
  }

  regiesterRoadAssistance(operationCode: string, productId?: string, structure?: string) {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, group} = this.calculateAutoParams(null);
    const { documentNumber, email } = {
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };

    return this.registerFailSafe(
      entityCode,
      productId || user.documentNumber,
      '',
      operationCode,
      group,
      structure || '',
      documentNumber,
      email
    );
  }

  private getROParams(policy: Policy) {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, group, codProduct } = this.getParamsByPolicyType(policy);
    return {
      entityCode,
      codProduct,
      productId: policy['product'] || 'product',
      group,
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  getAciLoginROQueryParams(user: string): string {
    const origin = this.getOrigin();
    const entityCode = '00';
    const groupCode = '0';

    return `?roEntityCode=${entityCode}&roGroupingCode=${groupCode}&roOrigin=${origin}&roDocumentNumber=${user}`;
  }

  getForgotPasswordROQueryParams({
    documentNumber,
    origin
  }) {
    const structure =
      origin === ForgotPasswordOrigin.FROM_LOGIN
        ? 'Login'
        : 'Olvido de contraseña';
    return {
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.getOrigin()}`,
      roProductId: structure,
      roDocumentNumber: documentNumber
    };
  }

  registerChangePatrimonialData(operationCode: string, productId: string, policy: Policy) {
    operationCode = OthersOperation[operationCode];
    const params = this.calculateOthersOperationParams(policy);
    return this.registerFailSafe(
      params.entityCode,
      productId || params.productId,
      params.codProduct,
      operationCode,
      params.group,
      ''
    );
  }

  private calculateProfileParams() {
    return {
      entityCode: '00',
      group: '0'
    };
  }

  private getParamsByPolicyType(
    policy: Policy
  ): {
    entityCode: string;
    group: string;
    codProduct: string;
    productId: string;
  } {
    switch (policy?.policyBaseType) {
      case 'car':
        return this.calculateAutoParams(policy);
      case 'home':
        return this.calculateHomeParams(policy);
      case 'other':
      case 'accident':
      case 'death':
        return this.calculateOthersOperationParams(policy);
      case 'saving':
      case 'life':
      case 'collective-life':
        return this.calculateLifeOperationParams(policy);
      case 'health':
        return {
          entityCode: '283',
          group: '04',
          codProduct: policy.productCode,
          productId: policy.product ?? ''
        };
      case 'pension':
        return this.calculatePensionParams(policy);
      default:
        break;
    }
    return {
      entityCode: '',
      group: '',
      codProduct: '',
      productId: ''
    };
  }

  private setHttpParams({
    entityCode,
    groupingCode,
    origin,
    productCode,
    productId,
    nuuma,
    userCode,
    email,
    documentNumber
  }): HttpParams {
    return createHttpParams({
      roEntityCode: entityCode,
      roGroupingCode: groupingCode,
      roOrigin: origin,
      roProductCode: productCode,
      roProductId: productId,
      roNuuma: nuuma,
      roUserCode: userCode,
      roEmail: email,
      roDocumentNumber: documentNumber
    });
  }

  private getReceiptPaymentDepartmentData(type?: string) {
    const mmobileKey = this.authMirror.authService.checkUserIsParticular() ?
      RECEIPT_PAYMENT_DEPARTMENT_DATA : RECEIPT_PAYMENT_COMPANY_DEPARTMENT_DATA;
    return this.mmobileHelper.getCustomConfigData(mmobileKey, type);
  }

  calculateDocumentROParams(policy, documentParams?: DocumentROParams) {
    const {
      user: { documentNumber, email }
    } = this.authMirror.authService.getState();

    const {
      entityCode,
      group,
      codProduct,
      productId
    } = this.getParamsByPolicyType(policy);

    let roParams = {
      documentNumber,
      email,
      codProduct,
      productId,
      entityCode,
      group
    };

    switch (documentParams?.type) {
      case DocumentTypes.RECEIPT:
        roParams = {
          ...roParams,
          productId: documentParams.payload?.receiptNumber
        };
        break;
      case DocumentTypes.DUPLICATE_FISCAL_CERTIFICATE:
        roParams = {
          ...roParams,
          codProduct: documentParams.payload.codProduct ?? roParams.codProduct,
          productId:
            documentParams.payload?.model ?? documentParams.payload?.exercise
        };
        break;
      case DocumentTypes.QUATERLY:
        roParams = {
          ...roParams,
          codProduct: documentParams.payload.codProduct ?? roParams.codProduct,
          productId: `${documentParams.payload.exercise} - ${documentParams.payload.period}`
        };
        break;
      case DocumentTypes.INBOX_DOCUMENT:
        roParams = {
          ...roParams,
          entityCode: '00',
          group: '0',
          productId: `${documentParams.payload.subject} - ${documentParams.payload.dateTime}`
        };
        break;
      case DocumentTypes.COPAYMENT:
        roParams = {
          ...roParams,
          productId: documentParams.payload?.copaymentDocumentNumber
        };
        break;
      default:
        break;
    }

    const body = this.buildOperationRequestBody(
      roParams.entityCode,
      roParams.productId,
      roParams.codProduct,
      '',
      roParams.group,
      '',
      roParams.documentNumber,
      roParams.email,
      policy
    );
    return this.setHttpParams(body);
  }

  setRobotOrigin() {
    this.isRobotOrigin = true;
  }

  getNonRenewalROParams(policy: Policy): NonRenewalROParams {
    const { user } = this.authMirror.authService.getState();
    const { entityCode, group, codProduct } =
    this.getParamsByPolicyType(policy);
    return {
      entityCode,
      codProduct,
      productId: policy['product'] || 'product',
      group,
      email: (user && user.email) || '',
      documentNumber: (user && user.documentNumber) || ''
    };
  }

  getDownloadContractROParams() {
    const { user } = this.authMirror.authService.getState();

    return {
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.getOrigin()}`,
      roProductId: '',
      roOperationCodePrefix: '8',
      roUserCode: user.userCode,
      roDocumentNumber: (user && user.documentNumber) || '',
      roEmail: (user && user.email) || ''
    };
  }

  getMailboxROParams(mail?: MailMessage) {
    const { user } = this.authMirror.authService.getState();
    let params = createHttpParams({
      roEntityCode: '00',
      roGroupingCode: '0',
      roOrigin: `${this.getOrigin()}`,
      roDocumentNumber: user?.documentNumber,
      roEmail: user?.email,
    });
    if (mail) {
      params = params.set('roProductId', `${mail?.subject} ${mail.dateTime}`);
    }
    return params;
  }

  registerNonRenewalOperations(
    policy: Policy,
    operationCode: string,
    structure: string
  ) {
    const params = this.getNonRenewalROParams(policy);

    const register = this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email
    );

    return register;
  }

  registerBusinessGeneralOperation(
    operationCode: string,
    structure?: {
      nif?: string,
      cif?: string,
      name?: string,
      surname1?: string,
      surname2?: string,
      email?: string,
      contactDetail?: string,
      socialReason?: string,
      entity?: string,
      entityName?: string,
      address?: Address,
      certificate?: BusinessCertificateInfo
    },
    businessDocumentNumber?: string
  ) {
    const entityCode = '00';
    const group = '0';

    let productId;

    productId = structure?.entityName ?? structure?.entity ?? '';

    const operationsWithIssuerCertificateProductId = [LoginWithCertificateOperation.CERTIFICATE_ERROR,
      LoginWithCertificateOperation.VALID_CERTIFICATE,
      ClientRegister.NOT_REPRESENTATIVE_CERTIFICATE,
      ClientRegister.VALID_CERTIFICATE
    ] as string[];

    if (operationsWithIssuerCertificateProductId.includes(operationCode)) {
      productId = structure?.certificate?.issuer?.O ?? structure.certificate?.issuer?.CN ?? '';
    }

    return this.registerFailSafe(
      entityCode,
      productId,
      '',
      operationCode,
      group,
      JSON.stringify(structure) || '',
      businessDocumentNumber,
      '',
    );
  }

  registerMultiMapOperation(
    operationCode: MultiMapOperation,
    product: string,
    structure?: string
  ) {
    const { user } = this.authMirror.authService.getState();
    const entityCode = '00';
    const group = '0';
    const productCode = '';
    return this.registerFailSafe(
      entityCode,
      product,
      productCode,
      operationCode,
      group,
      structure || '',
      user?.documentNumber,
      user?.email
    );
  }

  customerSignUpRO(
    operationCode: string,
    structure: string,
    state?: Partial<RegisterState>,
    productDetail?: string,
    user?: Partial<GetUserDataResponse>
  ) {
    const entityCode = '00';
    const group = '0';
    let product = `Campaña <Id de campaña> ACCESO ${state.campaingId}`.replace('<Id de campaña>', state.campaignDesc);
    if (productDetail) product += ' ' + productDetail;
    const documentNumber = state?.documentNumber ? state?.documentNumber : user?.documentNumber ? user.documentNumber : '';
    const email = state?.email ? state?.email : user?.email ? user.email : '';
    return this.registerFailSafe(
      entityCode,
      product,
      '',
      operationCode,
      group,
      structure,
      documentNumber,
      email
    );
  }

  /**
   * @return '' | 'nif' | '+cif' | 'nif+cif'
   */
  getRoDocumentNumber({nif = '', cif = ''}: {nif?: string, cif?: string}) {
    return (nif ?? '') + (cif ? `+${cif}` : '');
  }

  registerOperation(
    policy: Policy,
    operationCode: string,
    structure: string
  ) {
    const params = this.getROParams(policy);

    const register = this.registerFailSafe(
      params.entityCode,
      params.productId,
      params.codProduct,
      operationCode,
      params.group,
      structure,
      params.documentNumber,
      params.email
    );

    return register;
  }

  medicalCheckUpRO(
    operationCode: string,
    structure: string,
    state: Partial<MedicalExaminationState>,
    product: MedicalAccessOptionType
  ) {
    const entityCode = '398';
    const group = '4';

    return this.registerFailSafe(
      entityCode,
      product,
      '',
      operationCode,
      group,
      structure,
      state?.userCode ? state?.userCode : '',
      state?.email ? state?.email : ''
    );
  }

  public getChatUrlQueryParams() {
    const origin = this.getOrigin();
    const { user } = this.authMirror.authService.getState();
    const { documentNumber, email } = {
      email: user?.email || '',
      documentNumber: user?.documentNumber || ''
    };

    return new HttpParams()
    .set('roEntityCode', '00')
    .set('roGroupingCode', '0')
    .set('roOrigin', origin.toString())
    .set('roProductId', 'Posición Global')
    .set('roOperationCodePrefix', '8')
    .set('roDocumentNumber', documentNumber)
    .set('roEmail', email);
  }
}

